import React, { useState, useEffect, useRef } from 'react';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { InputText } from 'primereact/inputtext';

import { useTranslation } from 'react-i18next';

import { Toast } from 'primereact/toast';

import { FULL_DATE_FORMAT, TOAST_LIFETIME } from '../../settings';
import { Button } from 'primereact/button';
import { hasPermission } from '../_shared/hasPermission';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from '../../store';
import scalesService from '../../service/api/ScalesService';
import scaleOrdersService from '../../service/api/ScaleOrdersService';
import weighingsService from '../../service/api/WeighingsService';
import { scaleInfoActions } from '../../store/scale-info-slice';
import { useHistory, useParams } from 'react-router-dom';
import { Dropdown } from 'primereact/dropdown';
import OperatorAssignmentDialog from './OperatorAssignmentDialog';
import PackagedGoodsControlAssignmentDialog from './PackagedGoodsControlAssignmentDialog';
import { getColumnTemplates } from '../_shared/tableHelpers';
import { CommunicationMode } from '../../enums/communication-mode.enum';
import { ScaleIndication } from './ScaleIndication';
import { InputNumber } from 'primereact/inputnumber';
import { InputSwitch } from 'primereact/inputswitch';
import { ProgressBar } from 'primereact/progressbar';
import { modeContext } from '../../services/ModeContext';
import mqtt from 'mqtt';

export const ScaleInfoManagement = () => {
  const toast = useRef<any>();

  const [operatorDialogVisible, setOperatorDialogVisible] = useState(false);
  const [packagedGoodsControlDialogVisible, setPackagedGoodsControlDialogVisible] = useState(false);

  const { t } = useTranslation();
  const dispatch = useDispatch();
  const modeCtx = modeContext();

  const scaleInfo = useSelector((state: RootState) => state.scaleInfo);
  const scaleSettings = useSelector((state: RootState) => state.scaleInfo.scaleSettings);
  const measurements = useSelector((state: RootState) => state.scaleInfo.packagedGoodsControl.measurements);
  const history = useHistory();
  const columnTemplates = getColumnTemplates(t);

  const { scaleId } = useParams<any>();

  const loggedUserContext = useSelector((state: RootState) => state.user.context);

  const canSelectScale = hasPermission('PACKAGED_GOODS_CONTROL_SELECT_SCALE', loggedUserContext.permissions);
  const canChangeOperator = hasPermission('PACKAGED_GOODS_CONTROL_CHANGE_OPERATOR', loggedUserContext.permissions);
  const canChangeControl = hasPermission('PACKAGED_GOODS_CONTROL_CHANGE_CONTROL', loggedUserContext.permissions);
  const canChangeScaleSettings = hasPermission(
    'PACKAGED_GOODS_CONTROL_CHANGE_SCALE_SETTINGS',
    loggedUserContext.permissions
  );
  const availableScales = loggedUserContext.scales || [];

  const showToast = (summary: string, detail: string, severity: 'success' | 'info' | 'warn' | 'error') => {
    toast.current.show({
      severity: severity,
      summary: summary,
      detail: detail,
      life: TOAST_LIFETIME,
    });
  };

  const handleOperatorAssignmentSuccess = (operatorName: string | null) => {
    const message = operatorName
      ? t('scaleInfo.operator_assigned_successfully')
      : t('scaleInfo.operator_unassigned_successfully');
    showToast(t('common.success'), message, 'success');
  };

  const handlePackagedGoodsControlAssignmentSuccess = (packagedGoodsControlName: string | null) => {
    const message = packagedGoodsControlName
      ? t('scaleInfo.packaged_goods_control_assigned_successfully')
      : t('scaleInfo.packaged_goods_control_unassigned_successfully');
    showToast(t('common.success'), message, 'success');
  };

  const loadData = async (id) => {
    const scaleInfo = await scalesService.getScaleInfo(id);
    scaleInfo.operator = { id: scaleInfo.operatorId, name: scaleInfo.operatorName };

    dispatch(scaleInfoActions.setScaleInfo(scaleInfo));
  };

  useEffect(() => {
    if (!modeCtx.isOffline || !scaleId) {
      return () => {};
    }

    const mqttEndpoint = 'localhost:1884'; // TODO:: env

    const client = mqtt.connect(`ws://${mqttEndpoint}/mqtt`);

    client.on('message', (_topic, message) => {
      const data = JSON.parse(message.toString());
      dispatch(scaleInfoActions.setPackagedGoodsControlInfo(data.data));
    });

    client.on('connect', () => {
      console.log('Connected to MQTT broker');
      const topics = [`ui/${loggedUserContext.customerId}/${scaleId}/packagedGoodsControlUpdate`];
      if (scaleInfo?.packagedGoodsControl?.id) {
        topics.push(
          `ui/${loggedUserContext.customerId}/packagedGoodsControlUpdate/${scaleInfo.packagedGoodsControl.id}`
        );
      }

      client.subscribe(topics, (err) => {
        if (!err) {
          console.log(`Subscribed to topics: ${topics}`);
        } else {
          console.error(`Failed to subscribe to topics: ${topics}`, err);
        }
      });
    });

    return () => {
      client.end();
    };

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scaleId, scaleInfo.packagedGoodsControl.id]);

  useEffect(() => {
    if (scaleId) {
      loadData(scaleId);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scaleId]);

  const measurementCount = scaleInfo.packagedGoodsControl.measurementsCount;
  let expectedMeasurementCount = scaleInfo.packagedGoodsControl.controlTotalQuantity;
  if (
    scaleInfo.packagedGoodsControl.controlPhase1Quantity &&
    measurementCount < scaleInfo.packagedGoodsControl.controlPhase1Quantity
  ) {
    expectedMeasurementCount = scaleInfo.packagedGoodsControl.controlPhase1Quantity;
  }

  return (
    <div className="grid crud-demo">
      <div className="col-12">
        <div className="card">
          <div className="grid">
            {/* Left Section - Controls */}
            <div className="col-12 lg:col-6 align-items-center">
              <div className="mb-2">
                <Dropdown
                  id="scales"
                  options={availableScales}
                  optionLabel="name"
                  optionValue="id"
                  placeholder={t('common.select')}
                  value={scaleId}
                  onChange={(e) => {
                    history.push(`/scale-info/${e.value}`);
                  }}
                  className="w-full"
                  disabled={!canSelectScale}
                />
              </div>

              {/* Operator Section */}
              <div className="flex align-items-center gap-3 mb-2">
                <label htmlFor="currentOperator" className="font-medium white-space-nowrap mr-2">
                  {t('scaleInfo.current_operator')}
                </label>
                <div className="flex-grow-1">
                  <InputText
                    id="currentOperator"
                    value={scaleInfo.operator.name || ''}
                    disabled={true}
                    className="w-full"
                  />
                </div>
                <Button
                  type="button"
                  label={t('common.change')}
                  className="p-button-info p-button-sm ml-2"
                  disabled={!canChangeOperator || !scaleId}
                  onClick={() => {
                    setOperatorDialogVisible(true);
                  }}
                ></Button>
              </div>

              {/* Packaged Goods Control Section */}
              <div className="flex align-items-center gap-3 mb-2">
                <label htmlFor="currentPackagedGoodsControl" className="font-medium white-space-nowrap mr-2">
                  {t('scaleInfo.current_packaged_goods_control')}
                </label>
                <div className="flex-grow-1">
                  <InputText
                    id="currentPackagedGoodsControl"
                    value={scaleInfo.packagedGoodsControl.name || ''}
                    disabled={true}
                    className="w-full"
                  />
                </div>
                <Button
                  type="button"
                  label={t('common.change')}
                  className="p-button-info p-button-sm ml-2"
                  disabled={!canChangeControl || !scaleId}
                  onClick={() => {
                    setPackagedGoodsControlDialogVisible(true);
                  }}
                ></Button>
              </div>

              {/* Stable Measurement Toggle */}
              <div className="flex align-items-center gap-3">
                <label htmlFor="stableMeasurementToggle" className="font-medium white-space-nowrap mr-2">
                  {t('scaleInfo.stable_measurement_job')}
                </label>
                <InputSwitch
                  id="stableMeasurementToggle"
                  checked={scaleSettings.stableMeasurementEnabled || false}
                  onChange={(e) => {
                    if (e.value) {
                      scaleOrdersService.enableStableMeasurementJob(scaleId);
                    } else {
                      scaleOrdersService.disableStableMeasurementJob(scaleId);
                    }
                  }}
                  disabled={
                    !canChangeScaleSettings || scaleSettings.communicationMode !== CommunicationMode.TRIGGERED_BY_US
                  }
                />
              </div>
            </div>

            {/* Middle Section - Current Indication */}
            <div className="col-12 lg:col-4">
              <ScaleIndication scaleId={scaleId} unit={scaleInfo?.unit} />
            </div>

            {/* Right Section - Make Measurement Button */}
            <div className="col-12 lg:col-2 flex px-2">
              <Button
                type="button"
                label={t('scaleInfo.make_measurement')}
                className="p-button-success p-button-lg w-full h-full"
                disabled={
                  !scaleId ||
                  !scaleInfo.packagedGoodsControl?.id ||
                  scaleSettings.communicationMode !== CommunicationMode.TRIGGERED_BY_US ||
                  scaleSettings.stableMeasurementEnabled
                }
                onClick={() => {
                  weighingsService.savePackagedGoodsIndication(scaleId);
                }}
              ></Button>
            </div>
          </div>
          <div className="grid mt-4">
            <div className="col-3">
              <span className="p-float-label">
                <InputNumber
                  id="t1ErrorCount"
                  value={scaleInfo.packagedGoodsControl.t1ErrorCount || 0}
                  disabled={true}
                />
                <label htmlFor="t1ErrorCount">{t('scaleInfo.t1_error_count')}</label>
              </span>
            </div>
            <div className="col-3">
              <span className="p-float-label">
                <InputNumber
                  id="t2ErrorCount"
                  value={scaleInfo.packagedGoodsControl.t2ErrorCount || 0}
                  disabled={true}
                />
                <label htmlFor="t2ErrorCount">{t('scaleInfo.t2_error_count')}</label>
              </span>
            </div>
            <div className="col-3">
              <span className="p-float-label">
                <InputNumber
                  id="minAcceptableAvgValue"
                  value={scaleInfo.packagedGoodsControl.minAcceptableAvgValue || 0}
                  disabled={true}
                />
                <label htmlFor="minAcceptableAvgValue">{t('scaleInfo.min_acceptable_avg_value')}</label>
              </span>
            </div>
            <div className="col-3">
              <span className="p-float-label">
                <InputNumber id="avgValue" value={scaleInfo.packagedGoodsControl.avgValue || 0} disabled={true} />
                <label htmlFor="avgValue">{t('scaleInfo.avg_value')}</label>
              </span>
            </div>
          </div>
          {expectedMeasurementCount > 0 && (
            <div className="mb-3 mt-3">
              <ProgressBar
                value={expectedMeasurementCount > 0 ? (measurementCount / expectedMeasurementCount) * 100 : 0}
                displayValueTemplate={() => `${measurementCount} / ${expectedMeasurementCount}`}
              />
            </div>
          )}
          <DataTable
            value={measurements}
            paginator
            className="p-datatable-customers"
            rows={50}
            dataKey="id"
            rowHover
            emptyMessage={t('scaleInfo.no_measurements')}
            // header={tableHeader}
          >
            <Column
              field="scaleName"
              header={t('scaleInfo.scale_name')}
              sortable
              body={columnTemplates.bodyTemplate}
            ></Column>
            <Column
              field="operatorName"
              header={t('scaleInfo.operator_name')}
              sortable
              body={columnTemplates.bodyTemplate}
            ></Column>
            <Column
              field="timestamp"
              header={t('scaleInfo.timestamp')}
              sortable
              body={(data) => {
                const actualDate = data.timestamp ? new Date(data.timestamp) : null;
                return columnTemplates.calendarValueColumnTemplate(actualDate, FULL_DATE_FORMAT);
              }}
            ></Column>
            <Column
              field="value"
              header={`${t('scaleInfo.value')}`}
              sortable
              body={(data, props) => columnTemplates.numberTemplate(data, props, 'scaleUnit')}
            ></Column>
            <Column
              field="status"
              header={t('scaleInfo.status')}
              sortable
              body={(data, props) => columnTemplates.enumColumnTemplate(data, props, 'scaleInfo.statuses')}
            ></Column>
          </DataTable>
        </div>
      </div>

      <OperatorAssignmentDialog
        dialogVisible={operatorDialogVisible}
        closeModal={() => setOperatorDialogVisible(false)}
        scaleId={scaleId}
        currentOperator={scaleInfo.operator}
        onSuccess={handleOperatorAssignmentSuccess}
      />

      <PackagedGoodsControlAssignmentDialog
        dialogVisible={packagedGoodsControlDialogVisible}
        closeModal={() => setPackagedGoodsControlDialogVisible(false)}
        scaleId={scaleId}
        currentPackagedGoodsControl={scaleInfo.packagedGoodsControl}
        onSuccess={handlePackagedGoodsControlAssignmentSuccess}
      />

      <Toast ref={toast} />
    </div>
  );
};
